/**
 * Single source of truth for the visible pricing plans.
 *
 * Both the upgrade modal (`components/UpgradeModal.tsx`) and the standalone
 * subscription page (`pages/Subscription.tsx`) import `PRICING_PLANS` from
 * here so the two surfaces stay perfectly aligned. To change a plan name,
 * price, credit allotment, tagline, or feature ordering, edit this file.
 *
 * ────────────────────────────────────────────────────────────────────────
 * Server-side mirror: `server/credits.ts` (`PAID_PLAN_CYCLE_CREDITS`) and
 * `server/stripe.ts` (`SUBSCRIPTION_PLANS`). When you change a credit
 * allotment or price here, mirror it on the server too — the server is the
 * authority for billing, this file is the authority for display.
 * ────────────────────────────────────────────────────────────────────────
 *
 * Design choices baked into the feature list:
 *
 *   1. Music-video features come first (1080P, music analysis, commercial use,
 *      no watermarks). This puts the most
 *      differentiated value at the top and creates clean upgrade pressure
 *      ordering and creates clean upgrade pressure for the Free tier.
 *
 *   2. Song features (text-to-music, vocal removal, cover song, MP3) come
 *      after — we still support the original audio toolset, but it's not
 *      the headline value anymore.
 *
 *   3. We do NOT list underlying model names (Seedance / LTX / Nano Banana
 *      etc.). Credits abstract away which engine is used, and naming them
 *      ages the page every time we swap a provider.
 *
 *   4. `creditsBullets` translates the abstract credit number into concrete
 *      output ("Covers ~3-4 polished 1-min final video projects"). Phrasing
 *      stays approximate because video length varies and a flat "X videos"
 *      number can mislead.
 */

/**
 * A feature row inside a pricing card. We carry both an i18n `nameKey`
 * (resolved via `t(nameKey)` at render time) and a hard-coded English
 * `name` fallback. The fallback is used when a locale doesn't have the
 * key yet — i18n falls back to en, but we also accept the english string
 * directly through `defaultValue` to make the component robust.
 */
export type PlanFeature = {
  /** i18n key under `pricing.*` */
  nameKey: string;
  /** English fallback used as `defaultValue` */
  name: string;
  included: boolean;
};

export type PlanId = "free" | "plus" | "creator" | "pro_v2";

export interface PricingPlan {
  id: PlanId;
  /** English plan name. Looked up via `pricing.<id>` for localization. */
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  /** Monthly credit allotment (or daily for Free). */
  credits: number;
  /** Total annual credits (for the "X/year" footnote on yearly billing). */
  yearlyCredits: number;
  /**
   * Whether the headline credits label is "X daily credits" (free) or
   * "X monthly credits" (paid). The actual localized string is composed
   * at render time via `pricing.creditsDailyHeadline` /
   * `pricing.creditsMonthlyHeadline` with the count interpolated.
   */
  creditsCadence: "daily" | "monthly";
  /** Optional i18n key for a non-standard headline label. */
  creditsHeadlineKey?: string;
  /** English fallback for the headline credits line. */
  creditsLabel: string;
  /**
   * 1–3 short bullets translating credits → concrete output. Each entry
   * is an i18n key (under `pricing.*`) plus its English fallback. Some
   * entries carry an interpolation count (e.g. orSongsCount).
   */
  creditsBullets: Array<{
    key: string;
    text: string;
    count?: number;
  }>;
  /**
   * Mixed-include feature list. Lower tiers show some `included: false`
   * entries (rendered with ✗) so users see what they'd unlock by upgrading.
   */
  features: PlanFeature[];
  popular?: boolean;
}

/**
 * The canonical "core" feature axis used across all plan cards.
 * Paid tiers share all of it, including team support within 3 business days
 * (free users get in-app agent chat only). Keeping the
 * array shape identical means users can scan top-to-bottom and visually
 * compare which plan checks each row.
 */
// Shared feature key + fallback pairs so all plans cite the same
// canonical localizations.
const F = {
  // Locale keys keep their historical names (videoRendering720p/1080p) so
  // every translation stays wired up; the copy now reads 480p free and
  // 720p & 1080p paid.
  videoRendering480p: {
    nameKey: "pricing.videoRendering720p",
    name: "480p video rendering",
  },
  videoRendering720p1080p: {
    nameKey: "pricing.videoRendering1080p",
    name: "720p & 1080p video rendering",
  },
  commercialUse: {
    nameKey: "pricing.commercialUse",
    name: "Commercial use",
  },
  noWatermarks: {
    nameKey: "pricing.noWatermarks",
    name: "No watermarks",
  },
  aiSongGeneration: {
    nameKey: "pricing.aiSongGeneration",
    name: "AI song generation (text/lyrics)",
  },
  vocalRemovalCoverSong: {
    nameKey: "pricing.vocalRemovalCoverSong",
    name: "Vocal Removal & Cover Song",
  },
  downloadMp3: {
    nameKey: "pricing.downloadMp3Feature",
    name: "Download MP3",
  },
  // Free plan: downloads are allowed for personal, non-commercial use (the
  // commercialUse row above is what separates the tiers).
  downloadMp3Personal: {
    nameKey: "pricing.downloadMp3PersonalFeature",
    name: "Download MP3 (personal use)",
  },
  teamSupport: {
    nameKey: "pricing.teamSupport3Days",
    name: "Team support within 3 business days",
  },
};

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    credits: 10, // per day
    yearlyCredits: 10,
    creditsCadence: "daily",
    creditsHeadlineKey: "pricing.freeCreditsHeadline",
    creditsLabel:
      "100 music video credits, plus song credits that refill to 10 daily",
    creditsBullets: [
      {
        key: "pricing.freeOneTimeMusicVideo",
        text: "Starter credits cover one short 480p video with watermark",
      },
    ],
    features: [
      // Music-video axis (priority — same order on every card)
      { ...F.videoRendering480p, included: true },
      { ...F.commercialUse, included: false },
      { ...F.noWatermarks, included: false },
      // Song axis
      { ...F.aiSongGeneration, included: true },
      { ...F.vocalRemovalCoverSong, included: true },
      { ...F.downloadMp3Personal, included: true },
      // Support / perks
      { ...F.teamSupport, included: false },
    ],
  },
  {
    id: "plus",
    name: "Plus",
    monthlyPrice: 35,
    // $29/mo billed yearly = $348/year (17% off the $35/mo rate).
    yearlyPrice: 348,
    credits: 2000,
    yearlyCredits: 24000,
    creditsCadence: "monthly",
    popular: true,
    creditsLabel: "2,000 monthly credits",
    creditsBullets: [
      {
        key: "pricing.generateFullVideosPlus",
        text: "Generate ~3-7 full 1-min music videos",
      },
      {
        key: "pricing.orSongsCount",
        text: "Or 400 AI songs",
        count: 400,
      },
    ],
    features: [
      { ...F.videoRendering720p1080p, included: true },
      { ...F.commercialUse, included: true },
      { ...F.noWatermarks, included: true },
      { ...F.aiSongGeneration, included: true },
      { ...F.vocalRemovalCoverSong, included: true },
      { ...F.downloadMp3, included: true },
      { ...F.teamSupport, included: true },
    ],
  },
  {
    id: "creator",
    name: "Creator",
    monthlyPrice: 85,
    // $70/mo billed yearly = $840/year.
    yearlyPrice: 840,
    credits: 5500,
    yearlyCredits: 66000,
    creditsCadence: "monthly",
    creditsLabel: "5,500 monthly credits",
    creditsBullets: [
      {
        key: "pricing.generateFullVideosCreator",
        text: "Generate ~9-15 full 1-min music videos",
      },
      {
        key: "pricing.orSongsCount",
        text: "Or 1,100 AI songs",
        count: 1100,
      },
    ],
    features: [
      { ...F.videoRendering720p1080p, included: true },
      { ...F.commercialUse, included: true },
      { ...F.noWatermarks, included: true },
      { ...F.aiSongGeneration, included: true },
      { ...F.vocalRemovalCoverSong, included: true },
      { ...F.downloadMp3, included: true },
      { ...F.teamSupport, included: true },
    ],
  },
  {
    id: "pro_v2",
    name: "Pro",
    monthlyPrice: 135,
    // $112/mo billed yearly = $1,344/year (17% off the $135/mo rate).
    yearlyPrice: 1344,
    credits: 9000,
    yearlyCredits: 108000,
    creditsCadence: "monthly",
    creditsLabel: "9,000 monthly credits",
    creditsBullets: [
      {
        key: "pricing.generateFullVideosPro",
        text: "Generate ~15-25 full 1-min music videos",
      },
      {
        key: "pricing.orSongsCount",
        text: "Or 1,800 AI songs",
        count: 1800,
      },
    ],
    features: [
      { ...F.videoRendering720p1080p, included: true },
      { ...F.commercialUse, included: true },
      { ...F.noWatermarks, included: true },
      { ...F.aiSongGeneration, included: true },
      { ...F.vocalRemovalCoverSong, included: true },
      { ...F.downloadMp3, included: true },
      { ...F.teamSupport, included: true },
    ],
  },
];

/**
 * Tier rank used by upgrade/downgrade comparisons. Maps both legacy plan
 * keys (basic_*, standard_*, legacy pro_*) and current ones (free, plus,
 * pro_v2) onto the same numeric ladder.
 *
 *   free                       -> 0
 *   basic / plus               -> 1
 *   standard                   -> 1.25 (legacy mid-tier)
 *   creator                    -> 1.5
 *   pro (legacy) / pro_v2      -> 2
 */
export function getPlanTierIndex(planKey: string): number {
  const base = planKey
    .replace("_weekly", "")
    .replace("_monthly", "")
    .replace("_yearly", "");
  if (base === "free") return 0;
  if (base === "basic" || base === "plus") return 1;
  if (base === "standard") return 1.25;
  if (base === "creator") return 1.5;
  if (base === "pro" || base === "pro_v2") return 2;
  return 0;
}

/** Strip the "_monthly" / "_yearly" suffix from a server plan key. */
export function getBasePlan(plan: string): string {
  return plan
    .replace("_weekly", "")
    .replace("_monthly", "")
    .replace("_yearly", "");
}

/**
 * Suggest the default upgrade target for a user on `currentPlan`. Used by
 * the upgrade modal to preselect / scroll to the most likely conversion.
 */
export function getDefaultUpgradeTarget(currentPlan: string): PlanId {
  const tier = getPlanTierIndex(currentPlan);
  if (tier <= 1) return "plus";
  if (tier <= 1.5) return "creator";
  return "pro_v2";
}

/**
 * Order the top-up packs render in, cheapest first. Pack metadata (credits,
 * price, discount label) always comes from the server via
 * `payment.getCreditPacks` — this is display order only, and it lives here so
 * the four surfaces that render packs (UpgradeModal, Subscription,
 * CreditHistoryModal, landing PricingSection) can never drift apart.
 *
 * Mirrors `CREDIT_PACKS` in server/stripe.ts — adding a pack there means
 * adding its id here and to the `packId` enum in server/paymentRouter.ts.
 */
export const CREDIT_PACK_ORDER = [
  "small",
  "medium",
  "large",
  "mega",
  "ultra",
  "max",
] as const;

export type CreditPackId = (typeof CREDIT_PACK_ORDER)[number];

/**
 * The pack that gets the "Best Value" highlight — by definition the lowest
 * $/credit, so it moves whenever a cheaper tier is added. Mirrors
 * `BEST_VALUE_PACK_ID` in server/stripe.ts.
 */
export const BEST_VALUE_PACK_ID: CreditPackId = "max";
